import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { finalize } from 'rxjs';
import { LayoutService } from '../service/app.layout.service';
import {
  DEFAULT_MENU_STATE,
  LandingOption,
  MenuState,
  UiPreferences
} from './ui-preferences.model';
import { UiPreferencesService } from './ui-preferences.service';

type PrefsViewState = 'loading' | 'loaded' | 'error' | 'saving' | 'saved';

/**
 * GAP-UC-43 — Settings overlay panel body (topbar gear).
 *
 * Real integration (phase 8): loads/saves via `UiPreferencesService` (GET/PUT
 * api/ntap/ui/preferences) and sources landing options from the real role-filtered
 * menu. Template-driven forms + RxJS + PrimeNG 17 — matching the existing AppLayoutModule.
 * NgModule, zone-based, no signals (REQ-GAPUC43-018).
 */
@Component({
  selector: 'app-ui-preferences-panel',
  templateUrl: './app.ui-preferences-panel.component.html',
  styleUrls: ['./app.ui-preferences-panel.component.scss']
})
export class UiPreferencesPanelComponent implements OnInit {
  menuState: MenuState = DEFAULT_MENU_STATE; // REQ-003
  landingName: string | null = null; // stores MenuItem.name (REQ-007)

  /** Emitted when the panel requests the hosting overlay to close (Cancel / after save). */
  @Output() closed = new EventEmitter<void>();

  viewState: PrefsViewState = 'loading';
  landingOptions: LandingOption[] = []; // REQ-006/008 (navigable only)
  landingDisabled = false; // E1/REQ-010: zero navigable -> hide/disable
  validationError: string | null = null; // REQ-020 inline validation

  constructor(
    private readonly prefs: UiPreferencesService,
    private readonly layoutService: LayoutService
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  /** (Re)load options + saved prefs whenever the panel opens (REQ-012/013). */
  reload(): void {
    this.viewState = 'loading';
    this.validationError = null;

    // Landing options from the real role-filtered menu (REQ-006/008).
    this.prefs.landingOptions().subscribe({
      next: (opts) => {
        this.landingOptions = opts;
        this.landingDisabled = opts.length === 0; // E1/REQ-010
      },
      error: () => {
        // A menu failure only affects the landing control; keep it disabled + noted.
        this.landingOptions = [];
        this.landingDisabled = true;
      }
    });

    // Saved preferences (REQ-012). Absent record -> 200 with null fields (api-contract §5.1).
    this.prefs.load().subscribe({
      next: (v) => this.applyLoaded(v),
      error: () => (this.viewState = 'error') // E3/REQ-015: fall back, non-blocking
    });
  }

  private applyLoaded(v: UiPreferences): void {
    this.menuState = v.defaultMenuState ?? DEFAULT_MENU_STATE;
    this.landingName = v.defaultLandingName;
    this.viewState = 'loaded';

    // A1/REQ-009: exactly one navigable item -> it is the effective default.
    if (this.landingOptions.length === 1 && this.landingName == null) {
      this.landingName = this.landingOptions[0].value;
    }
  }

  save(): void {
    this.validationError = null;
    if (this.menuState !== 'COLLAPSED' && this.menuState !== 'EXPANDED') {
      // Client mirror of the contract VALIDATION_ERROR (§7); backend also enforces.
      this.validationError = 'Odaberite zadano stanje izbornika.';
      return;
    }

    this.viewState = 'saving';
    const body = {
      defaultMenuState: this.menuState,
      // Clear landing when the control is hidden (E1) so we never persist a stale value.
      defaultLandingName: this.landingDisabled ? null : this.landingName
    };
    this.prefs
      .save(body)
      .pipe(finalize(() => undefined))
      .subscribe({
        next: (stored) => {
          this.viewState = 'saved'; // REQ-011 (per-user persist)
          // Apply the chosen menu-state default immediately for this session.
          this.applyMenuState(stored.defaultMenuState ?? this.menuState);
        },
        error: () => (this.viewState = 'error') // E3/REQ-015
      });
  }

  /** Discard unsaved edits, re-load last stored state, and close the overlay (Cancel/Odustani). */
  cancel(): void {
    this.reload();
    this.closed.emit();
  }

  /**
   * Apply the default left-menu state to the running shell (REQ-003/004/005).
   * Only the left navigation menu default is touched — the transient toggle is unchanged.
   * `staticMenuDesktopInactive === true` means the static left menu starts collapsed.
   */
  private applyMenuState(state: MenuState): void {
    this.layoutService.state.staticMenuDesktopInactive = state === 'COLLAPSED';
  }
}
